import type { Product } from './product';
import type { Farmer } from './farmer';
import type { Organization } from './organization';
import type { User } from './user';

// Quote status enum matching Prisma schema
export type QuoteStatus = 
  | 'TEMPLATE'
  | 'DRAFT' 
  | 'PROPOSAL'
  | 'UNDER_REVIEW'
  | 'APPROVED'
  | 'EXECUTED'
  | 'REJECTED'
  | 'EXPIRED';

// Participant role enum
export type ParticipantRole = 'POLICYHOLDER' | 'INSURED';

// Bulk upload status enum
export type BulkUploadStatus = 
  | 'UPLOADED'
  | 'VALIDATED' 
  | 'PENDING_USER_DECISION'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'REJECTED';

// Bulk upload user decision enum
export type BulkUploadUserDecision = 'ACCEPT_VALID' | 'REJECT_ALL' | 'PENDING';

// Coverage item type enum
export type CoverageItemType = 
  | 'loan_credit'
  | 'input_agreement'
  | 'farm_asset'
  | 'activity_product_value'
  | 'household_sustenance';

// Protection levels configuration
export interface ProtectionLevel {
  strike_level: number;
  exhaust_level: number;
  strike_value: number;
  exhaust_value: number;
  units: string;
}

export interface ProtectionLevels {
  [perilName: string]: ProtectionLevel;
}

// Location validation results
export interface LocationValidation {
  validation_performed: boolean;
  all_within_coverage: boolean;
  total_locations: number;
  valid_locations: number;
  invalid_locations: Array<{
    farmer_id: string;
    coordinates: [number, number];
    distance_from_coverage: number;
    reason: string;
  }>;
  coverage_area_used: string;
  validated_at: string;
}

// Coverage items configuration
export interface CoverageItem {
  type: CoverageItemType;
  selected: boolean;
  amount?: number;
  currency?: string;
}

// Enhanced underlying assets configuration
export interface UnderlyingAssets {
  activities_products?: Array<{
    type: string;
    area_hectares?: number;
    expected_yield?: number;
    value_per_unit?: number;
  }>;
  legacy_assets?: string[]; // Backward compatibility
  assets?: string[];  // Legacy field
  items?: string[];   // Legacy field
}

// Bulk upload validation errors
export interface BulkUploadValidationError {
  row_index: number;
  row_data: Record<string, unknown>;
  errors: Array<{
    field: string;
    message: string;
  }>;
}

export interface BulkUploadDuplicateRow {
  row_index: number;
  row_data: Record<string, unknown>;
  duplicate_of: {
    farmer_id: string;
    name: string;
  };
  match_criteria: string[];
}

export interface BulkUploadValidationErrors {
  summary: {
    total_rows: number;
    valid_rows: number;
    invalid_rows: number;
    duplicate_rows: number;
  };
  invalid_rows: BulkUploadValidationError[];
  duplicate_rows: BulkUploadDuplicateRow[];
  validation_rules_applied: string[];
}

// Farmer bulk upload interface
export interface FarmerBulkUpload {
  id: string;
  quoteId: string;
  filename: string;
  filePath: string;
  tempFilePath?: string;
  totalRecords: number;
  validRecords: number;
  invalidRecords: number;
  processedRecords: number;
  successfulRecords: number;
  failedRecords: number;
  status: BulkUploadStatus;
  validationErrors?: BulkUploadValidationErrors;
  processingResults?: Record<string, unknown>;
  userDecision?: BulkUploadUserDecision;
  userDecisionAt?: Date;
  uploadedBy?: string;
  processedBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Peril configuration
export interface PerilConfiguration {
  peril_id: string;
  weight: number;
  // Note: Made optional since these fields are not used in current implementation
  payout_model?: string;
  parameters?: {
    threshold?: number;
    measurement_period?: string;
    max_payout?: number;
    windows?: Array<{
      start: string;
      end: string;
      multiplier: number;
      min_trigger?: number;
    }>;
  };
  data_source?: {
    provider: string;
    resolution: string;
    quality_threshold: number;
  };
}

// Perils configuration
export interface PerilsConfig {
  perils: PerilConfiguration[];
  total_weight_check: number; // Must equal 100.0
}

// Calculation results from R-Server
export interface CalculationResults {
  calculations: {
    net_risk_premium: number;
    underwriter_loading: number;
    gross_risk_premium: number;
    agent_commission: number;
    broker_commission: number;
    subsidies: number;
    final_premium: number;
    administrative_fee: number;
    taxes: number;
  };
  risk_metrics: {
    expected_loss_ratio: number;
    coefficient_of_variation: number;
    value_at_risk_95: number;
    tail_conditional_expectation: number;
  };
  calculation_metadata: {
    version: string;
    timestamp: string;
    model_parameters: Record<string, string | number | boolean>;
    data_vintage: string;
    confidence_level: number;
  };
  validation_checks: {
    premium_reasonableness: boolean;
    risk_concentration: boolean;
    data_quality: boolean;
    model_stability: boolean;
  };
}

// Quote participant relationship
export interface QuoteParticipant {
  id: string;
  quoteId: string;
  farmerId?: string;
  organizationId?: string;
  role: ParticipantRole;
  isActive: boolean;
  sumInsured?: number;
  hectaresAnimalCount?: number;
  premiumAmount?: number;
  paymentDetails?: Record<string, unknown>;
  bulkUploadId?: string;
  bulkUploadRowIndex?: number;
  createdAt: Date;
  farmer?: Farmer;
  organization?: Organization;
  bulkUpload?: FarmerBulkUpload;
}

// Main Quote interface
export interface Quote {
  id: string;
  quoteNumber: string;
  templateName?: string;
  status: QuoteStatus; // Will be removed - use state instead
  state: 'new' | 'approved' | 'ready_for_payment_collection' | 'payment_initiated' | 'activated' | 'partial_payment_received' | 'payment_collection_expired'; // State machine state (v2) - replaces status
  notes?: string;
  coverageStartDate: Date;
  coverageEndDate: Date;
  strikeLevel: number;
  isMasterPolicy: boolean;
  underlying_assets: UnderlyingAssets;
  perils_config: PerilsConfig;
  calculation_results?: CalculationResults;
  protectionLevels?: ProtectionLevels;
  locationValidation?: LocationValidation;
  coverageItems?: CoverageItem[];
  
  // Quotation calculation results
  quotationJobId?: string;
  quotationResults?: unknown;
  selectedScenarios?: Record<string, unknown>;
  
  // Pricing fields
  sumInsured: number;
  hectaresAnimalCount?: number;
  premiumAmount: number;
  
  // Wizard navigation
  currentWizardStep?: number;
  
  validUntil: Date;
  approvedAt?: Date;
  approvedBy?: string;
  rejectedReason?: string;
  
  // Relationships
  productId: string;
  organizationId: string;
  createdBy?: string;
  updatedBy?: string;
  
  // Populated relationships
  product: Product;
  organization: Organization;
  participants: QuoteParticipant[];
  createdByUser?: User;
  updatedByUser?: User;
  approvedByUser?: User;
  
  // Pre-calculated display values
  participantCount?: number;
  displaySumInsured?: number;
  displayPremium?: number;
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

// Quote filters for search and filtering
export interface QuoteFilters {
  status?: QuoteStatus;
  productId?: string;
  organizationId?: string;
  participantId?: string;
  search?: string;
  isTemplate?: boolean;
  coveragePeriod?: {
    start?: string;
    end?: string;
  };
}

// Quote creation/update payload
// Draft quote request - minimal data for early creation
export interface CreateDraftQuoteRequest {
  templateName?: string;
  productId: string;
  isMasterPolicy?: boolean;
  underlying_activities?: string[];
  coverageItems?: Array<{ type: string; selected: boolean }>;
  currentWizardStep?: number;
  notes?: string;
}

export interface CreateQuoteRequest {
  templateName?: string;
  productId: string;
  participants: Array<{
    farmerId?: string;
    organizationId?: string;
    role: ParticipantRole;
  }>;
  coverageStartDate: string;
  coverageEndDate: string;
  strikeLevel: number;
  isMasterPolicy?: boolean;
  underlying_assets: UnderlyingAssets;
  perils_config: PerilsConfig;
  notes?: string;
}

export interface UpdateQuoteRequest {
  templateName?: string;
  status?: QuoteStatus;
  coverageStartDate?: string;
  coverageEndDate?: string;
  strikeLevel?: number;
  isMasterPolicy?: boolean;
  // ALL JSON fields now consistently use data wrappers
  underlyingAssets?: {
    data: UnderlyingAssets;
    schemaId?: string;
    schemaVersion?: string;
  };
  perilsConfig?: {
    data: PerilsConfig;
    schemaId?: string;
    schemaVersion?: string;
  };
  coverageItems?: {
    data: Array<{ type: string; selected: boolean }>;
    schemaId?: string;
    schemaVersion?: string;
  };
  protectionLevels?: {
    data: Record<string, unknown>;
    schemaId?: string;
    schemaVersion?: string;
  };
  locationValidation?: {
    data: Record<string, unknown>;
    schemaId?: string;
    schemaVersion?: string;
  };
  calculationResults?: {
    data: unknown;
    schemaId?: string;
    schemaVersion?: string;
  };
  quotationResults?: {
    data: unknown;
    schemaId?: string;
    schemaVersion?: string;
  };
  selectedScenarios?: {
    data: Record<string, unknown>;
    schemaId?: string;
    schemaVersion?: string;
  };
  participants?: Array<{ farmerId?: string; organizationId?: string; role: 'POLICYHOLDER' | 'INSURED'; sumInsured?: number; hectaresAnimalCount?: number }>;
  sumInsured?: number;
  hectaresAnimalCount?: number;
  quotationJobId?: string;
  premiumAmount?: number;
  pricingBreakdown?: Record<string, unknown>;
  currentWizardStep?: number;
  notes?: string;
}

// Quotation aggregates interfaces
export interface QuotationAggregateTotal {
  strike_threshold: number;
  exhaust_threshold: number;
  ANRP: number;
  GRP: number;
  tax_amount: number;
  subsidy_amount: number;
  total_payable: number;
  payment_per_mm: number;
}

export interface QuotationAggregateParticipant {
  farmerId: string;
  strike_threshold: number;
  exhaust_threshold: number;
  ANRP: number;
  GRP: number;
  tax_amount: number;
  subsidy_amount: number;
  total_payable: number;
  payment_per_mm: number;
}

export interface QuotationAggregates {
  total: QuotationAggregateTotal;
  participants: QuotationAggregateParticipant[];
}

export interface QuotationSummaryResponse {
  quotationResults: {
    aggregates: QuotationAggregates;
    job_id: string;
    status: string;
    timestamp: string;
    // Raw output excluded for bandwidth efficiency
  };
  protectionLevels: Record<string, unknown>;
}

// API response types
export interface QuoteListResponse {
  quotes: Quote[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface QuoteDetailResponse {
  quote: Quote;
}

// Bulk upload API types
export interface BulkUploadCreateRequest {
  quoteId: string;
  file: File;
}

export interface BulkUploadCreateResponse {
  upload: FarmerBulkUpload;
  message: string;
}

export interface BulkUploadValidationResponse {
  upload: FarmerBulkUpload;
  validationResults: BulkUploadValidationErrors;
}

export interface BulkUploadUserDecisionRequest {
  decision: BulkUploadUserDecision;
}

export interface BulkUploadProcessResponse {
  upload: FarmerBulkUpload;
  results: {
    created_farmers: number;
    updated_participants: number;
    errors: Array<{
      row_index: number;
      error: string;
    }>;
  };
}

export interface BulkUploadTemplateResponse {
  templateUrl: string;
  filename: string;
  headers: string[];
  sampleData: Record<string, unknown>[];
}

// Display helper types
export interface QuoteDisplayInfo extends Quote {
  participantCount: number;
  primaryParticipant?: QuoteParticipant;
  coverageDuration: number; // in days
  daysUntilExpiry: number;
  canEdit: boolean;
  canApprove: boolean;
  canExecute: boolean;
}

// Status-specific quote labels
export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  TEMPLATE: 'Template',
  DRAFT: 'Draft',
  PROPOSAL: 'Proposal',
  UNDER_REVIEW: 'Under Review',
  APPROVED: 'Approved',
  EXECUTED: 'Executed',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
};

// Status colors for UI
export const QUOTE_STATUS_COLORS: Record<QuoteStatus, string> = {
  TEMPLATE: 'gray',
  DRAFT: 'yellow',
  PROPOSAL: 'blue',
  UNDER_REVIEW: 'purple',
  APPROVED: 'green',
  EXECUTED: 'emerald',
  REJECTED: 'red',
  EXPIRED: 'gray',
};

// State-specific quote labels (v2 state machine)
export const QUOTE_STATE_LABELS: Record<string, string> = {
  created: 'Draft',
  new: 'Draft',
  approved: 'Approved',
  ready_for_payment_collection: 'Ready for Collection',
  payment_initiated: 'Collection Initiated',
  activated: 'Active Policy',
  partial_payment_received: 'Partial Payment',
  payment_collection_expired: 'Payment Expired',
};

// State colors for UI (v2 state machine)
export const QUOTE_STATE_COLORS: Record<string, string> = {
  created: 'yellow',
  new: 'yellow',
  approved: 'green',
  ready_for_payment_collection: 'blue',
  payment_initiated: 'orange',
  activated: 'purple',
  partial_payment_received: 'orange',
  payment_collection_expired: 'red',
};

// Helper functions for state-based logic
export const isQuoteReadOnly = (quote: Quote): boolean => {
  // Quote is read-only when approved or in later states
  return quote.state !== 'new';
};

export const canQuoteBeApproved = (quote: Quote): boolean => {
  return quote.state === 'new';
};

export const canQuoteBeExecuted = (quote: Quote): boolean => {
  return quote.state === 'approved' || quote.state === 'ready_for_payment_collection';
};

// Participant role labels
export const PARTICIPANT_ROLE_LABELS: Record<ParticipantRole, string> = {
  POLICYHOLDER: 'Policyholder',
  INSURED: 'Insured',
};

// Bulk upload status labels
export const BULK_UPLOAD_STATUS_LABELS: Record<BulkUploadStatus, string> = {
  UPLOADED: 'Uploaded',
  VALIDATED: 'Validated',
  PENDING_USER_DECISION: 'Pending Decision',
  PROCESSING: 'Processing',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  REJECTED: 'Rejected',
};

// Bulk upload status colors
export const BULK_UPLOAD_STATUS_COLORS: Record<BulkUploadStatus, string> = {
  UPLOADED: 'blue',
  VALIDATED: 'yellow',
  PENDING_USER_DECISION: 'orange',
  PROCESSING: 'purple',
  COMPLETED: 'green',
  FAILED: 'red',
  REJECTED: 'gray',
};

// Coverage item type labels
export const COVERAGE_ITEM_LABELS: Record<CoverageItemType, string> = {
  loan_credit: 'Loan/Credit',
  input_agreement: 'Input Agreement',
  farm_asset: 'Farm Asset',
  activity_product_value: 'Activity/Product Value',
  household_sustenance: 'Household Sustenance',
}; 